import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Upload, Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 40.27 — small drop-in widget that lets a student upload a photo of
 * their handwritten answer for a specific question. Reads → base64 → posts to
 * /api/board-exams/ocr-grade (or /api/competitive-exams/ocr-grade based on
 * examSource) → returns the graded result to the parent via onGraded.
 */
export const HandwrittenUploader = ({ examId, questionIndex, examSource = 'board', onGraded }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pickFile = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error('Image too large — max 8 MB');
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error('Please upload a PNG, JPEG, or WEBP image.');
      return;
    }
    setBusy(true);
    try {
      // Read as base64.
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result); // data:image/...;base64,XXXX
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      const token = localStorage.getItem('token');
      const endpoint = examSource === 'competitive'
        ? `${API}/api/competitive-exams/ocr-grade`
        : `${API}/api/board-exams/ocr-grade`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          exam_id: examId,
          question_index: questionIndex,
          image_base64: b64,
          exam_source: examSource,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const graded = await res.json();
      onGraded?.(graded);
    } catch (err) {
      toast.error(err.message || 'Failed to grade handwritten answer');
    } finally {
      setBusy(false);
      // Reset the input so the same file can be re-picked if needed.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="mt-3 flex items-center gap-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFile}
        className="hidden"
        data-testid="handwritten-upload-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={pickFile}
        className="border-primary/40"
        data-testid="handwritten-upload-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
        {busy ? 'Reading & grading…' : 'Upload handwritten answer'}
      </Button>
      <span className="text-[11px] text-muted-foreground">PNG / JPG · max 8 MB · AI will OCR & grade</span>
    </div>
  );
};

export default HandwrittenUploader;
